import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { EntityCard } from '../components/dashboard/EntityCard'
import { QuickStats } from '../components/dashboard/QuickStats'
import { RecentActivity } from '../components/dashboard/RecentActivity'
import type { Entity, Transaction, DashboardStats } from '../types'
import { useEntities } from '../hooks/useEntities'
import type { Entity as APIEntity } from '../types/entities'

interface APITransaction {
  id: string
  entityId?: string
  date?: string
  amount?: number
  description?: string
  category?: string
  confidence?: number
  needsReview?: boolean
}

function parseDateMs(value?: string): number | null {
  if (!value) return null
  const ms = Date.parse(value)
  return Number.isNaN(ms) ? null : ms
}

function mapCategoryToType(category: string | undefined, amount: number): Transaction['type'] {
  if (!category) return amount >= 0 ? 'revenue' : 'expense'
  switch (category) {
    case 'capital_contribution':
    case 'distribution':
    case 'member_distribution':
    case 'revenue':
    case 'expense':
    case 'tax_payment':
    case 'intercompany_sale':
      return category
    default:
      return amount >= 0 ? 'revenue' : 'expense'
  }
}

function mapEntityToCard(entity: APIEntity, balance: number, transactionCount: number): Entity {
  return {
    id: entity.id,
    name: entity.name,
    displayName: entity.shortName || entity.name,
    type: entity.entityType as Entity['type'],
    balance,
    transactionCount,
  }
}

export function Dashboard() {
  const navigate = useNavigate()
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [rawTransactions, setRawTransactions] = useState<APITransaction[]>([])
  const [txLoading, setTxLoading] = useState(true)
  const [txError, setTxError] = useState<string | null>(null)
  const { data: apiEntities = [], isLoading: entitiesLoading, error: entitiesError } = useEntities()
  const [stats, setStats] = useState<DashboardStats>({
    totalPortfolioValue: 0,
    transactionsToday: 0,
    transactionsThisWeek: 0,
    pendingReviews: 0
  })

  useEffect(() => {
    let cancelled = false
    setTxLoading(true)
    setTxError(null)

    fetch('/api/transactions?limit=50', { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) {
          const message = await res.text()
          throw new Error(message || `Failed to load transactions (${res.status})`)
        }
        return res.json() as Promise<APITransaction[]>
      })
      .then((data) => {
        if (cancelled) return
        const normalized = Array.isArray(data) ? data : []
        setRawTransactions(normalized)
        const mapped: Transaction[] = normalized.map((tx) => {
          const amount = typeof tx.amount === 'number' ? tx.amount : 0
          return {
            id: tx.id,
            date: tx.date || new Date(0).toISOString(),
            amount,
            counterparty: tx.description || 'Unknown',
            type: mapCategoryToType(tx.category, amount),
            confidence: typeof tx.confidence === 'number' ? tx.confidence : undefined,
          }
        })
        setTransactions(mapped)
      })
      .catch((err) => {
        if (cancelled) return
        setRawTransactions([])
        setTransactions([])
        setTxError(err instanceof Error ? err.message : 'Failed to load transactions')
      })
      .finally(() => {
        if (!cancelled) setTxLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const entities = useMemo<Entity[]>(() => {
    const now = Date.now()
    const dayStart = new Date(new Date().toDateString()).getTime()
    const balanceByEntity = new Map<string, number>()
    const txCountTodayByEntity = new Map<string, number>()

    for (const tx of rawTransactions) {
      const entityId = tx.entityId
      if (!entityId) continue
      const amount = typeof tx.amount === 'number' ? tx.amount : 0
      balanceByEntity.set(entityId, (balanceByEntity.get(entityId) || 0) + amount)
      const dateMs = parseDateMs(tx.date)
      if (dateMs !== null && dateMs >= dayStart && dateMs <= now) {
        txCountTodayByEntity.set(entityId, (txCountTodayByEntity.get(entityId) || 0) + 1)
      }
    }

    return apiEntities.map((entity) =>
      mapEntityToCard(
        entity,
        balanceByEntity.get(entity.id) || 0,
        txCountTodayByEntity.get(entity.id) || 0,
      ),
    )
  }, [apiEntities, rawTransactions])

  useEffect(() => {
    const now = Date.now()
    const dayStart = new Date(new Date().toDateString()).getTime()
    const weekStart = now - (7 * 24 * 60 * 60 * 1000)

    let transactionsToday = 0
    let transactionsThisWeek = 0
    let pendingReviews = 0

    for (const tx of rawTransactions) {
      const dateMs = parseDateMs(tx.date)
      if (dateMs !== null && dateMs >= dayStart && dateMs <= now) {
        transactionsToday += 1
      }
      if (dateMs !== null && dateMs >= weekStart && dateMs <= now) {
        transactionsThisWeek += 1
      }
      if (tx.needsReview || (typeof tx.confidence === 'number' && tx.confidence < 0.8)) {
        pendingReviews += 1
      }
    }

    const totalPortfolioValue = entities.reduce((sum, entity) => sum + (entity.balance || 0), 0)
    setStats({
      totalPortfolioValue,
      transactionsToday,
      transactionsThisWeek,
      pendingReviews,
    })
  }, [entities, rawTransactions])

  const isLoading = entitiesLoading || txLoading
  const errorMessage =
    txError ||
    (entitiesError instanceof Error ? entitiesError.message : null)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-swiss-body text-swiss-gray-500">Loading dashboard...</div>
      </div>
    )
  }

  if (errorMessage) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-swiss-body text-swiss-red">{errorMessage}</div>
      </div>
    )
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-[24px] font-semibold tracking-tight leading-[1.2]">Dashboard</h1>
        <p className="text-swiss-gray-500 mt-2 text-[14px]">
          Portfolio overview and daily updates
        </p>
      </div>

      {/* Quick Stats */}
      <div className="mb-12">
        <QuickStats stats={stats} />
      </div>

      {/* Alert Banner for Pending Reviews */}
      {stats.pendingReviews > 0 && (
        <div className="flex items-center justify-between py-5 px-6 bg-swiss-off-white border-l-[3px] border-l-swiss-red mb-12">
          <div className="flex items-center gap-4">
            <div className="w-5 h-5 bg-swiss-red text-swiss-white flex items-center justify-center text-swiss-caption font-bold">
              {stats.pendingReviews}
            </div>
            <div className="text-swiss-small">
              {stats.pendingReviews} transaction{stats.pendingReviews !== 1 ? 's' : ''} require manual review
            </div>
          </div>
          <button
            onClick={() => navigate('/transactions')}
            aria-label="Review pending transactions requiring manual review"
            className="text-swiss-caption font-medium text-swiss-black underline underline-offset-2 cursor-pointer"
          >
            Review Now
          </button>
        </div>
      )}

      {/* Entity Cards Section */}
      <div className="mb-12">
        <div className="flex justify-between items-baseline mb-6 pb-4 border-b border-swiss-gray-200">
          <h2 className="text-swiss-label uppercase tracking-widest text-swiss-gray-500">
            Portfolio Entities
          </h2>
          <button
            onClick={() => navigate('/entities')}
            className="text-swiss-caption text-swiss-gray-500 cursor-pointer transition-colors duration-150 hover:text-swiss-black"
          >
            Manage Entities →
          </button>
        </div>

        {/* Entity Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-swiss-gray-200 border border-swiss-gray-200">
          {entities.map((entity) => (
            <EntityCard
              key={entity.id}
              entity={entity}
              onClick={() => navigate(`/entities/${entity.id}`)}
            />
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="mb-12">
        <RecentActivity
          transactions={transactions}
          onViewAll={() => navigate('/transactions')}
        />
      </div>
    </div>
  )
}
